import { useState, useRef, useLayoutEffect, useCallback } from "react";
import type { ToggletipPlacement } from "./types";

interface UseToggletipOptions {
  open: boolean;
  direction: ToggletipPlacement;
  offset: number;
  autoDirection?: boolean;
}

interface UseToggletipReturn {
  refs: {
    setReference: (node: HTMLElement | null) => void;
    setFloating: (node: HTMLElement | null) => void;
  };
  direction: ToggletipPlacement;
}

const getScrollParent = (element: HTMLElement | null): HTMLElement => {
  if (!element || element === document.body) {
    return document.body;
  }

  const { overflow, overflowX, overflowY } = window.getComputedStyle(element);
  const scrollRegex = /(auto|scroll)/;
  const isScrollable =
    scrollRegex.test(overflow) ||
    scrollRegex.test(overflowX) ||
    scrollRegex.test(overflowY);

  if (
    isScrollable &&
    (element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth)
  ) {
    return element;
  }

  return getScrollParent(element.parentElement);
};

const getViewportBounds = (triggerElement: HTMLElement) => {
  const scrollParent = getScrollParent(triggerElement);

  if (scrollParent === document.body) {
    return {
      top: 0,
      left: 0,
      right: window.visualViewport?.width || window.innerWidth,
      bottom: window.visualViewport?.height || window.innerHeight,
      width: window.visualViewport?.width || window.innerWidth,
      height: window.visualViewport?.height || window.innerHeight,
    };
  }

  const rect = scrollParent.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
};

const checkCollision = (
  triggerRect: DOMRect,
  toggletipRect: DOMRect,
  placement: ToggletipPlacement,
  offset: number,
  triggerElement: HTMLElement
): ToggletipPlacement => {
  const viewport = getViewportBounds(triggerElement);
  const buffer = 10;

  const space = {
    top: triggerRect.top - viewport.top,
    bottom: viewport.bottom - triggerRect.bottom,
    left: triggerRect.left - viewport.left,
    right: viewport.right - triggerRect.right,
  };

  const centerY = triggerRect.top + triggerRect.height / 2;
  const centerX = triggerRect.left + triggerRect.width / 2;
  const halfWidth = toggletipRect.width / 2;
  const halfHeight = toggletipRect.height / 2;
  const minSpace = offset + buffer;

  const fits = {
    top:
      space.top >= toggletipRect.height + minSpace &&
      centerX - halfWidth >= viewport.left + buffer &&
      centerX + halfWidth <= viewport.right - buffer,
    bottom:
      space.bottom >= toggletipRect.height + minSpace &&
      centerX - halfWidth >= viewport.left + buffer &&
      centerX + halfWidth <= viewport.right - buffer,
    left:
      space.left >= toggletipRect.width + minSpace &&
      centerY - halfHeight >= viewport.top + buffer &&
      centerY + halfHeight <= viewport.bottom - buffer,
    right:
      space.right >= toggletipRect.width + minSpace &&
      centerY - halfHeight >= viewport.top + buffer &&
      centerY + halfHeight <= viewport.bottom - buffer,
  };

  const [primary, secondary] = placement.split("-");

  if (fits[primary as keyof typeof fits]) {
    return placement;
  }

  const opposites: Record<string, string> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };

  const opposite = opposites[primary];
  if (opposite && fits[opposite as keyof typeof fits]) {
    return secondary
      ? (`${opposite}-${secondary}` as ToggletipPlacement)
      : (opposite as ToggletipPlacement);
  }

  const perpendicular =
    primary === "top" || primary === "bottom"
      ? ["right", "left"]
      : ["bottom", "top"];

  for (const dir of perpendicular) {
    if (fits[dir as keyof typeof fits]) {
      return dir as ToggletipPlacement;
    }
  }

  const best = Object.entries(space).sort(([, a], [, b]) => b - a)[0];
  return best[0] as ToggletipPlacement;
};

export const useToggletip = ({
  open,
  direction,
  offset,
  autoDirection = true,
}: UseToggletipOptions): UseToggletipReturn => {
  const [newDirection, setNewDirection] = useState(direction);
  const referenceRef = useRef<HTMLElement | null>(null);
  const floatingRef = useRef<HTMLElement | null>(null);

  const setReference = useCallback((node: HTMLElement | null) => {
    referenceRef.current = node;
  }, []);

  const setFloating = useCallback((node: HTMLElement | null) => {
    floatingRef.current = node;
  }, []);

  // Collision detection + continuous updates
  useLayoutEffect(() => {
    if (
      !open ||
      !autoDirection ||
      !referenceRef.current ||
      !floatingRef.current
    ) {
      return;
    }

    let rafId: number | null = null;

    const updatePlacement = () => {
      rafId = null;
      if (!referenceRef.current || !floatingRef.current) return;

      const triggerRect = referenceRef.current.getBoundingClientRect();
      const toggletipRect = floatingRef.current.getBoundingClientRect();
      const calculatedDirection = checkCollision(
        triggerRect,
        toggletipRect,
        direction,
        offset,
        referenceRef.current
      );

      setNewDirection((prev) =>
        prev === calculatedDirection ? prev : calculatedDirection
      );
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(updatePlacement);
    };

    updatePlacement();

    // Attach scroll listener to the actual scroll parent for better performance
    const scrollParent = getScrollParent(referenceRef.current);
    const scrollTarget = scrollParent === document.body ? window : scrollParent;

    scrollTarget.addEventListener("scroll", scheduleUpdate, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", scheduleUpdate);

    // Safe ResizeObserver usage for SSR compatibility
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      if (referenceRef.current) {
        resizeObserver.observe(referenceRef.current);
      }
      if (floatingRef.current) {
        resizeObserver.observe(floatingRef.current);
      }
    }

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      scrollTarget.removeEventListener("scroll", scheduleUpdate, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", scheduleUpdate);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [open, autoDirection, direction, offset]);

  useLayoutEffect(() => {
    if (!open) {
      setNewDirection(direction);
    }
  }, [open, direction]);

  return {
    refs: {
      setReference,
      setFloating,
    },
    direction: newDirection,
  };
};
